import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, DollarSign, Clock, Activity, AlertCircle } from 'lucide-react';
import apiService from '../services/api';
import DashboardCharts from './DashboardCharts';

const Dashboard = ({ patients: propPatients }) => {
  // State management for dashboard data
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalPatients: 0,
      totalVisits: 0,
      totalRevenue: 0,
      pendingTreatments: 0
    },
    recentPatients: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load dashboard data on component mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  /**
   * Fetches dashboard statistics and recent patients from API
   * Falls back to calculating from propPatients if API fails
   */
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      let stats = null;
      let recentPatients = [];

      // Attempt to fetch stats from API
      try {
        stats = await apiService.getDashboardStats();
      } catch (statsError) {
        console.warn('Dashboard stats API not available:', statsError.message);
      }

      // Attempt to fetch recent patients from API
      try {
        const patientsResponse = await apiService.getRecentPatients();
        recentPatients = Array.isArray(patientsResponse) ? patientsResponse :
                        (patientsResponse?.data && Array.isArray(patientsResponse.data)) ? patientsResponse.data : [];
      } catch (patientsError) {
        console.warn('Recent patients API not available:', patientsError.message);
      }

      // Fallback to calculating stats from props if API failed
      if (!stats) {
        stats = calculateStatsFromPatients(propPatients || []);
      }

      // Fallback to using propPatients if API failed
      if (recentPatients.length === 0 && propPatients && Array.isArray(propPatients)) {
        recentPatients = propPatients.slice(-5).reverse();
      }

      setDashboardData({
        stats,
        recentPatients: Array.isArray(recentPatients) ? recentPatients : []
      });

    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.message);

      // Use fallback data on error
      const fallbackStats = calculateStatsFromPatients(propPatients || []);
      const fallbackPatients = propPatients && Array.isArray(propPatients) ?
                              propPatients.slice(-5).reverse() : [];

      setDashboardData({
        stats: fallbackStats,
        recentPatients: fallbackPatients
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculates dashboard statistics from patient data
   * Used as fallback when API is unavailable
   */
  const calculateStatsFromPatients = (patients) => {
    if (!Array.isArray(patients)) {
      return {
        totalPatients: 0,
        totalVisits: 0,
        totalRevenue: 0,
        pendingTreatments: 0
      };
    }

    const totalPatients = patients.length;
    
    // Calculate total visits across all patients
    const totalVisits = patients.reduce((sum, patient) =>
      sum + (patient.visits ? patient.visits.length : 0), 0
    );
    
    // Calculate total revenue from all visits
    const totalRevenue = patients.reduce((sum, patient) =>
      sum + (patient.visits ? patient.visits.reduce((visitSum, visit) =>
        visitSum + (visit.fee || 0), 0
      ) : 0), 0
    );
    
    // Count pending treatments
    const pendingTreatments = patients.reduce((sum, patient) =>
      sum + (patient.treatmentRemarks ?
        patient.treatmentRemarks.filter(remark => remark.status === 'pending').length : 0
      ), 0
    );

    return {
      totalPatients,
      totalVisits,
      totalRevenue,
      pendingTreatments
    };
  };

  // Loading state UI
  if (loading) {
    return (
      <div className="space-y-6 px-4 sm:px-0">
        {/* Loading Header */}
        <div className="card border-l-4 border-l-brand-blue">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-3/4 mb-3"></div>
            <div className="h-4 bg-slate-200 rounded w-1/2"></div>
          </div>
        </div>

        {/* Loading Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card">
              <div className="animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-2/3 mb-3"></div>
                <div className="h-8 bg-slate-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Loading Charts */}
        <div className="card">
          <div className="animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3].map(j => (
                <div key={j} className="h-16 bg-slate-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { stats, recentPatients } = dashboardData;

  return (
    <div className="space-y-6 px-4 sm:px-0">
      {/* Welcome Header - Professional Medical Style */}
      <div className="card border-l-4 border-l-brand-blue bg-gradient-to-r from-blue-50 to-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 mb-1">
              Welcome to Nova Dent
            </h1>
            <p className="text-slate-600 font-sans text-sm sm:text-base font-medium">
              Patient Management Dashboard • {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <Activity className="h-12 w-12 text-brand-blue opacity-20 hidden sm:block" />
        </div>
      </div>

      {/* Statistics Cards - Professional Medical Design */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Patients Card */}
        <div className="card border-b-4 border-b-brand-blue hover:shadow-lg transition-all">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-sans font-bold text-slate-500 uppercase tracking-widest mb-2">
                Total Patients
              </p>
              <h3 className="text-4xl font-display font-bold text-slate-900 stat-number">
                {stats.totalPatients || 0}
              </h3>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <Users className="h-7 w-7 text-brand-blue" />
            </div>
          </div>
        </div>

        {/* Total Visits Card */}
        <div className="card border-b-4 border-b-emerald-500 hover:shadow-lg transition-all">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-sans font-bold text-slate-500 uppercase tracking-widest mb-2">
                Total Visits
              </p>
              <h3 className="text-4xl font-display font-bold text-slate-900 stat-number">
                {stats.totalVisits || 0}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 rounded-xl">
              <Calendar className="h-7 w-7 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* Total Revenue Card */}
        <div className="card border-b-4 border-b-brand-red hover:shadow-lg transition-all">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-sans font-bold text-slate-500 uppercase tracking-widest mb-2">
                Total Revenue
              </p>
              <h3 className="text-2xl font-display font-bold text-slate-900 stat-number">
                {(stats.totalRevenue || 0).toLocaleString()} 
                <span className="text-sm text-slate-600 font-sans ml-1">MMK</span>
              </h3>
            </div>
            <div className="p-3 bg-red-50 rounded-xl">
              <DollarSign className="h-7 w-7 text-brand-red" />
            </div>
          </div>
        </div>

        {/* Pending Treatments Card */}
        <div className="card border-b-4 border-b-amber-500 hover:shadow-lg transition-all">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-sans font-bold text-slate-500 uppercase tracking-widest mb-2">
                Pending Treatments
              </p>
              <h3 className="text-4xl font-display font-bold text-slate-900 stat-number">
                {stats.pendingTreatments || 0}
              </h3>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl">
              <Clock className="h-7 w-7 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Visual Statistics Charts - Full Width */}
      <div>
        <DashboardCharts patients={propPatients || []} />
      </div>

      {/* Recent Patients - Compact List */}
      <div className="card">
        <h2 className="text-xl font-display font-bold text-slate-900 mb-4 flex items-center">
          <div className="w-1 h-6 bg-brand-blue rounded-full mr-3"></div>
          Recent Patients
        </h2>
        {Array.isArray(recentPatients) && recentPatients.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {recentPatients.map(patient => (
              <Link
                key={patient.id}
                to={`/patient/${patient.id}`}
                className="group flex items-center p-3 border-2 border-slate-200 hover:border-brand-blue hover:bg-blue-50 rounded-xl transition-all"
              >
                {/* Patient Avatar with first letter of name */}
                <div className="w-10 h-10 bg-gradient-to-br from-brand-blue to-blue-600 text-white rounded-full flex items-center justify-center font-display font-bold text-base shadow-sm flex-shrink-0">
                  {(patient.full_name || patient.fullName || 'U').charAt(0).toUpperCase()}
                </div>
                
                {/* Patient Information */}
                <div className="ml-3 min-w-0 flex-1">
                  <p className="font-sans font-bold text-sm text-slate-900 group-hover:text-brand-blue transition-colors truncate">
                    {patient.full_name || patient.fullName || 'Unknown Patient'}
                  </p>
                  <p className="text-xs text-slate-500 font-sans truncate">
                    {patient.phone || 'No phone'}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          // Empty state when no patients exist
          <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 mb-1 font-sans font-semibold">No patients registered yet</p>
            <p className="text-sm text-slate-400 font-sans mb-4">Start by registering your first patient</p>
            <Link 
              to="/register" 
              className="inline-flex items-center text-brand-blue hover:text-blue-700 font-sans font-bold text-sm group"
            >
              Register First Patient 
              <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>
        )}
      </div>

      {/* System Status - Professional Footer */}
      <div className="card bg-slate-50 border-2 border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div>
            <h3 className="text-sm font-display font-bold text-slate-700 mb-2 flex items-center">
              <AlertCircle className="h-4 w-4 mr-2 text-slate-500" />
              System Status
            </h3>
            <div className="flex flex-wrap items-center gap-4">
              {/* System Online Indicator */}
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full mr-2 animate-pulse shadow-sm"></div>
                <span className="text-sm text-slate-600 font-sans font-semibold">System Online</span>
              </div>
              
              {/* API Connection Status */}
              <div className="flex items-center">
                <div className={`w-2.5 h-2.5 rounded-full mr-2 shadow-sm ${error ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500 animate-pulse'}`}></div>
                <span className="text-sm text-slate-600 font-sans font-semibold">
                  {error ? 'API Offline (Demo Mode)' : 'API Connected'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Retry Button - Only shown when there's an error */}
          {error && (
            <button
              onClick={loadDashboardData}
              className="px-5 py-2.5 bg-brand-blue text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-sans font-bold shadow-sm hover:shadow-md flex items-center"
            >
              <Activity className="h-4 w-4 mr-2" />
              Retry Connection
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;